/**
 * ConversationChat Component
 * Chat interface for context-aware RAG queries
 * Validates: Phase 3 - Context Management
 */
import { useState, useRef, useEffect } from 'react';
import { useConversationStore } from '../../stores/conversationStore';
import { useDocumentStore } from '../../stores/documentStore';
import type { Conversation, DocumentQueryResult } from '../../types';

interface ConversationChatProps {
  conversation: Conversation;
}

export function ConversationChat({ conversation }: ConversationChatProps) {
  const [query, setQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [topK, setTopK] = useState(5);
  const [useHybridSearch, setUseHybridSearch] = useState(true);
  const [queryResults, setQueryResults] = useState<DocumentQueryResult[]>([]);

  // Search options (defaults for hybrid search)
  const fusionMethod: 'rrf' | 'weighted' | 'max' = 'rrf';
  const enableReRanking = true;
  const reRankAlgorithm: 'mmr' | 'relevance' | 'metadata' | 'recency' | 'combined' = 'mmr';
  const enableExpansion = true;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { queryInConversation, isQuerying, contextInfo, fetchContextInfo, updateTitle } = useConversationStore();
  const { documents } = useDocumentStore();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [query]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  // Fetch context info on mount
  useEffect(() => {
    fetchContextInfo(conversation.id).catch((err) => console.error('Failed to fetch context info:', err));
  }, [conversation.id, fetchContextInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isQuerying) return;

    try {
      const result = await queryInConversation(conversation.id, query.trim(), {
        documentIds: selectedDocs.length > 0 ? selectedDocs : undefined,
        topK,
        useHybridSearch,
        searchOptions: useHybridSearch
          ? {
              enableExpansion,
              enableReRanking,
              fusionMethod,
              reRankAlgorithm,
            }
          : undefined,
      });

      setQueryResults(result.queryResults);
      setQuery('');
    } catch (error) {
      console.error('Query failed:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  const toggleDocSelection = (docId: string) => {
    setSelectedDocs((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const getContextColor = () => {
    if (!contextInfo) return 'text-gray-400';
    if (contextInfo.contextWindowUsage > 80) return 'text-red-400';
    if (contextInfo.contextWindowUsage > 60) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getContextBarColor = () => {
    if (!contextInfo) return 'bg-gray-600';
    if (contextInfo.contextWindowUsage > 80) return 'bg-red-500';
    if (contextInfo.contextWindowUsage > 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-gray-800/50 rounded-lg flex flex-col h-full">
      {/* Header with Title and Context Info */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <input
            type="text"
            value={conversation.title}
            onChange={(e) => updateTitle(conversation.id, e.target.value)}
            className="text-lg font-semibold text-white bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-2"
          />
          <span className="text-xs text-gray-400">
            {conversation.messages.length} messages
          </span>
        </div>

        {/* Context Window Indicator */}
        {contextInfo && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Context Window</span>
              <span className={getContextColor()}>
                {contextInfo.contextWindowUsage.toFixed(1)}% used
              </span>
            </div>
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${getContextBarColor()}`}
                style={{ width: `${contextInfo.contextWindowUsage}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{contextInfo.totalTokens.toLocaleString()} tokens</span>
              <span>{contextInfo.remainingTokens.toLocaleString()} remaining</span>
            </div>
            {contextInfo.needsPruning && (
              <div className="text-xs text-yellow-400 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Old messages may be pruned
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {conversation.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Start a conversation by asking a question below
          </div>
        ) : (
          <>
            {conversation.messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                    <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                    {message.tokenCount && (
                      <span className="ml-2">{message.tokenCount} tokens</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Query Results */}
      {queryResults.length > 0 && (
        <div className="border-t border-gray-700 p-4 max-h-64 overflow-y-auto">
          <h3 className="text-sm font-medium text-white mb-2">Retrieved Documents ({queryResults.length})</h3>
          <div className="space-y-2">
            {queryResults.map((result, index) => (
              <div key={index} className="bg-gray-900 rounded p-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-medium">{result.documentName}</span>
                  <span className="text-gray-400">{Math.round(result.score * 100)}%</span>
                </div>
                <p className="text-gray-400 line-clamp-2">{result.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-gray-700 p-4">
        {/* Document Selection */}
        {documents.length > 0 && (
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Search in documents:</label>
            <div className="flex flex-wrap gap-2">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => toggleDocSelection(doc.id)}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    selectedDocs.includes(doc.id)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {doc.name.length > 15 ? `${doc.name.substring(0, 15)}...` : doc.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-2">
          <textarea
            ref={textareaRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isQuerying}
            placeholder="Ask a question..."
            className="w-full bg-gray-900 text-white rounded-lg p-3 resize-none border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 text-sm"
            rows={2}
          />

          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1 text-gray-400">
                <input
                  type="checkbox"
                  checked={useHybridSearch}
                  onChange={(e) => setUseHybridSearch(e.target.checked)}
                  className="rounded"
                />
                Hybrid Search
              </label>
              <label className="flex items-center gap-1 text-gray-400">
                Results:
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={topK}
                  onChange={(e) => setTopK(Number(e.target.value))}
                  className="w-12 bg-gray-900 border border-gray-700 rounded px-1 text-white"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={!query.trim() || isQuerying}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isQuerying ? 'Searching...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
